import React, { useCallback, useContext, useMemo, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { Link } from 'react-router-dom';

import AllDefinitionsGate from 'components/AllDefinitionsGate';
import usePopupReports, {
  isDashboardPopupReportUsage,
  isGoalPopupReportUsage,
  isKpiListPopupReportUsage,
  PopupReportUsage,
} from '../../../../../contextProviders/SplashScreenProviders/ContentDependantProviders/PopupReportUsageLookupProvider/usePopupReports';
import usePopup from '../../../../../hooks/usePopup';
import Typography from '../../../../../kingpin/atoms/Typography';
import Button from '../../../../../kingpin/atoms/Button';
import Form from 'kingpin/forms/Form';
import FormHeader from '../../../../../kingpin/forms/FormHeader';
import FormContent from '../../../../../kingpin/forms/FormContent';
import Row from '../../../../../components/Common/Row';
import FormInput from '../../../../../kingpin/forms/FormInput';
import Dropdown from '../../../../../components/Inputs/Dropdown';
import Alert from '../../../../../kingpin/Alert';
import _ from 'lodash';
import STORE from '../../../../../store';
import AccountPickerContext from '../../../../../contexts/AccountPickerContext';
import LabelsPicker from '../../../../../components/Inputs/LabelsPicker';
import ToastContext from '../../../../../contexts/ToastContext';
import Inputs from '../../../../../components/Inputs';
import Tooltip from '../../../../../components/Tooltip';

const DashboardUsage = ({ usage }: { usage: PopupReportUsage[] }) => {
  const dashboardsUsage = useMemo(() => {
    return _.uniqBy(usage.filter(isDashboardPopupReportUsage), 'dashboard.id');
  }, [usage]);

  if (dashboardsUsage.length === 0) {
    return null;
  }

  return (
    <FormInput label={'Dashboards'} isInputSection>
      {dashboardsUsage.map((item) => (
        <div style={{ marginBottom: 4 }} key={item.dashboard.id}>
          <Link to={`/content/${item.dashboard.id}?ui=v3`}>
            <Typography.Body type={'Link'}>
              {item.dashboard.name}
            </Typography.Body>
          </Link>
        </div>
      ))}
    </FormInput>
  );
};

const KpiListUsage = ({ usage }: { usage: PopupReportUsage[] }) => {
  const kpiListUsage = useMemo(() => {
    return _.uniqBy(usage.filter(isKpiListPopupReportUsage), 'kpiList.id');
  }, [usage]);

  if (kpiListUsage.length === 0) {
    return null;
  }

  return (
    <FormInput label={'KPI Lists'} isInputSection>
      {kpiListUsage.map((item) => (
        <div style={{ marginBottom: 4 }} key={item.kpiList.id}>
          <Link to={`/content/${item.kpiList.id}?ui=v3`}>
            <Typography.Body type={'Link'}>
              {item.kpiList.title}
            </Typography.Body>
          </Link>
        </div>
      ))}
    </FormInput>
  );
};

const GoalUsage = ({ usage }: { usage: PopupReportUsage[] }) => {
  const goalsUsage = useMemo(() => {
    return _.uniqBy(usage.filter(isGoalPopupReportUsage), 'goal.id');
  }, [usage]);

  if (goalsUsage.length === 0) {
    return null;
  }

  return (
    <FormInput label={'Goals'} isInputSection>
      {goalsUsage.map((item) => (
        <div style={{ marginBottom: 4 }} key={item.goal.id}>
          <Link to={`/content/${item.goal.id}?ui=v3`}>
            <Typography.Body type={'Link'}>{item.goal.title}</Typography.Body>
          </Link>
        </div>
      ))}
    </FormInput>
  );
};

export const PopupReportUsageInfo = ({
  usage,
}: {
  usage: PopupReportUsage[];
}) => {
  return (
    <>
      <DashboardUsage usage={usage} />
      <KpiListUsage usage={usage} />
      <GoalUsage usage={usage} />
    </>
  );
};

const V3PopupReportsMigratorPopup = ({ close }: { close: () => void }) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { showToast } = useContext(ToastContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedReports, setSelectedReports] = React.useState<ReportType[]>(
    [],
  );
  const [onlyFromPublishedSources, setOnlyFromPublishedSources] =
    React.useState<boolean>(true);
  const popupReports = usePopupReports({ onlyFromPublishedSources });
  const privatePopupReports = useMemo(() => {
    return popupReports.filter((r) => !r.report.isV3Published);
  }, [popupReports]);

  const dropdownOptions = useMemo((): DropdownOption[] => {
    return privatePopupReports.map((report) => ({
      key: report.report.id,
      label: report.report.name,
      hasDividerAfter: true,
      renderLabel: () => (
        <div
          key={report.report.id}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <FormInput label={'Popup Report Name:'}>
            <Typography.Body type={'Annotation'}>
              {report.report.name}
            </Typography.Body>
          </FormInput>

          <PopupReportUsageInfo usage={report.usage} />
        </div>
      ),
      isSelected: selectedReports.some((r) => r.id === report.report.id),
      onSelected: () => {
        setSelectedReports((currentReports) => {
          if (currentReports.some((r) => r.id === report.report.id)) {
            return currentReports.filter((r) => r.id !== report.report.id);
          }
          return [...currentReports, report.report];
        });
      },
    }));
  }, [privatePopupReports, selectedReports]);
  const [labelIds, setLabelIds] = React.useState<string[]>([]);

  const onMigrateClicked = useCallback(async () => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    const promises = selectedReports.map((report) => {
      const newReport: ReportType = {
        ...report,
        isV3Published: true,
        labelIds: _.uniq([...report.labelIds, ...labelIds]),
      };

      return STORE.getReportsRef({
        accountId: selectedAccountId,
      })
        .doc(newReport.id)
        .set(newReport);
    });

    await Promise.all(promises);
    setIsLoading(false);
    showToast(`${selectedReports.length} have been published`);
    close();
  }, [
    close,
    isLoading,
    labelIds,
    selectedAccountId,
    selectedReports,
    showToast,
  ]);

  return (
    <Form>
      <FormHeader title={'Migrate Popup Reports'} onClose={close} />
      <FormContent>
        <FormInput label={'Only show popups found on published assets'}>
          <div style={{ marginBottom: 8 }}>
            <Alert
              type={'Info'}
              body={
                'Published assets considered are; Dashboard Gadgets, Metric Lists, KPI Lists and Goals'
              }
            />
          </div>
          <Tooltip
            content={
              selectedReports.length > 0
                ? 'You may only use this if no reports are selected'
                : undefined
            }
          >
            <Inputs.Toggle
              isDisabled={selectedReports.length > 0}
              value={onlyFromPublishedSources}
              onChange={setOnlyFromPublishedSources}
            />
          </Tooltip>
        </FormInput>
        <FormInput label={'Select Popup Reports'}>
          <div style={{ marginBottom: 8 }}>
            <Alert
              type={'Info'}
              body={
                'The following reports are set to private on V3 and are found as a popup on a dashboard gadget, kpi list or goal'
              }
            />
          </div>

          <Dropdown
            options={dropdownOptions}
            isMulti
            isSearchEnabled
            placeholder={'Select reports...'}
          />
        </FormInput>
        <FormInput label={'Labels to be applied'}>
          <div style={{ marginBottom: 8 }}>
            <Alert
              type={'Info'}
              body={
                'These labels will be attached to any selected reports for migration. These will be added to any existing labels on the report.'
              }
            />
          </div>
          <LabelsPicker labelIds={labelIds} setLabelIds={setLabelIds} />
        </FormInput>
      </FormContent>
      <Row spaceBetween centerAlign>
        <div />
        <Button
          size={'Medium'}
          type={'Primary'}
          onClick={onMigrateClicked}
          isDisabled={isLoading}
          isLoading={isLoading}
          label={'Migrate'}
        />
      </Row>
    </Form>
  );
};

const V3PopupReportsMigrator = () => {
  const { isOpen, open, close } = usePopup();

  return (
    <>
      <Button
        type={'Tertiary'}
        size={'Medium'}
        onClick={open}
        label={'Migrate Popup Reports'}
      />

      <ModalTransition>
        {isOpen && (
          <Modal
            onClose={close}
            shouldScrollInViewport={false}
            autoFocus={false}
          >
            <AllDefinitionsGate>
              <V3PopupReportsMigratorPopup close={close} />
            </AllDefinitionsGate>
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};

export default V3PopupReportsMigrator;
